import { Typography, useTheme } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DistroPlus } from 'components/DistroLabels';
import LoadingButton from 'components/LoadingButton';
import { SessionFlow } from 'services/api/entities';
import { distributionInfo } from 'services/api/stripe/stripeApi';
import { getHomePageUrl } from 'services/helpers/domain';
import { useStripeSession } from 'services/hooks';
import { getCheckoutURL } from 'services/hooks/stripe/helpers';
import FeaturesList from '../FeaturesList';
import CloseButton from '../CloseButton';
import {
  StyledCicularProgress,
  StyledDialogContent,
  StyledDivider,
  StyledHeader,
  StyledStack,
  StyledSubtitle,
  StyledTitle,
} from './styled';

type PropsType = {
  onClose: () => void;
  songId?: string;
  releaseId?: string;
};
const UpsellModalContent = ({ onClose, songId, releaseId }: PropsType) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { isLoading, createUserPortalSession } = useStripeSession();
  const { data, isLoading: isLoadingUpsellData } =
    distributionInfo.useGetDistributionInfoQuery();

  const handleAddDistribution = useCallback(() => {
    const returnUrl = getHomePageUrl();
    const successUrl = getCheckoutURL();
    createUserPortalSession({
      returnUrl,
      flowType: SessionFlow.UPDATE,
      successUrl,
      songId,
      releaseId,
      isDistributionUpsellFlow: true,
    });
  }, [songId, releaseId, createUserPortalSession]);

  if (isLoadingUpsellData || !data) {
    return <StyledCicularProgress size={theme.spacing(8)} />;
  }

  const { distroPlus } = data;
  const { features } = distroPlus;

  return (
    <StyledDialogContent data-testid="upsell-modal-description">
      <StyledHeader>
        <StyledTitle>{t('distribution:upsellModal:title')}</StyledTitle>
        <CloseButton onClose={onClose} />
      </StyledHeader>
      <StyledSubtitle>
        {t('distribution:upsellModal:subtitle')}
        <DistroPlus isLightBackground />
      </StyledSubtitle>
      <StyledDivider />
      <LoadingButton
        variant="rounded"
        loading={isLoading}
        disabled={isLoading}
        onClick={handleAddDistribution}
        data-testid="upsell-modal-submit-button"
      >
        {t('distribution:upsellModal:buttonText')}
      </LoadingButton>
      <StyledDivider />
      <StyledStack>
        <DistroPlus isLightBackground />
        <Typography>{t('distribution:upsellModal:includes')}</Typography>
      </StyledStack>
      <FeaturesList features={features} />
    </StyledDialogContent>
  );
};

export default UpsellModalContent;
